import { LoadMethodOptions, Query, ResultSet } from '@cubejs-client/core';
import { ConfigService, Inject, Injectable } from '@produce8/pegasus';
import { QueryErrorMessages } from '../../../common/enum/cube-error-message.enum';
import { StaticEnvironmentKeysEnum } from '../../../common/enum/static-environment/static-environment-keys.enum';
import { DiLogManager } from '../../../common/port/log/di-log.manager.port';
import { CubeConnectionPool } from './cube-connection-pool';
import { ICubeAdapter } from './cube.adapter.interface';

@Injectable()
export class DiCubeAdapter implements ICubeAdapter {
  private connectionPool: CubeConnectionPool;

  constructor(
    @Inject(DiLogManager) protected readonly logManager: DiLogManager,
    @Inject(ConfigService) protected readonly configService: ConfigService
  ) {
    const endpoint = this.configService.get<string>(
      StaticEnvironmentKeysEnum.CUBEJS_API_ENDPOINT
    );
    this.connectionPool = new CubeConnectionPool(this.logManager, endpoint);
    this.logManager.debug('Client initialized with connection pooling');
  }

  /**
   * Generic cube api wrapper method. For use in Domain repositories only.
   * @param token {string} CUBE API Token
   * @param data {Cube.Query} CUBE Query object
   * @param options? {Cube.LoadMethodOptions} Optional CUBE Query method options object
   * @returns Cube api response
   */
  async query(
    token: string,
    data: Query,
    options?: LoadMethodOptions
  ): Promise<any[]> {
    let client;

    try {
      client = await this.connectionPool.getConnection(token);
      this.logManager.debug(
        'calling api with data',
        JSON.stringify(data, null, 4)
      );
      const response: ResultSet = await client.load(data, {
        ...options,
        castNumerics: false,
      });
      this.logManager.debug('response', response.rawData());
      return response.rawData();
    } catch (error) {
      this.logManager.debug('Caught error in query', error);
      throw new Error(`${QueryErrorMessages.ApiCallError}: ${error}`);
    } finally {
      if (client) {
        this.connectionPool.releaseConnection(token, client);
      }
    }
  }

  /**
   * Get connection pool statistics
   */
  getPoolStats(): Record<string, any> {
    return this.connectionPool.getStats();
  }

  /**
   * Close the connection pool
   */
  async close(): Promise<void> {
    await this.connectionPool.close();
  }
}
